import { Box, IconButton, styled, TableCell, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { cast } from '~/utils/helper';
import { RotationLock, EditLineIcon, RotationLock2, DeleteBin6LineIcon, RotationLock3 } from '~/assets/icons';
import dayjs from 'dayjs';
import { IUser } from '~/utils/interface/user';
import BaseTable from '~/components/common/base-table/BaseTable';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { userActions } from '~/thunks/user/userSlice';
import { PAGE_INDEX, PAGE_SIZE } from '~/utils/constants/common';
import { ActiveEnum, RoleIdUserEnum } from '~/utils/enum';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: '#404854',
    boxShadow: '0px 4px 12px #509EE833',
    border: 1,
    fontSize: theme.typography.pxToRem(15),
  },
}));

type UserListProps = {
  onOpenEdit?: (user: IUser) => void;
  onOpenActive?: (user: IUser) => void;
  onOpenDelete?: (user: IUser) => void;
  onResetPassword?: (user: IUser) => void;
};

const UserList = (props: UserListProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/user');

  const { users, totalItems, filter } = useAppSelector((state) => state.user);

  const headCells: Array<HeadCelType> = [
    {
      id: 'id',
      label: t('id'),
      sortable: false,
      searchable: false,
      className: 'w-16 border-l border-[#EBF1F8] border-b-0 sticky left-0 z-10',
    },
    {
      id: 'action',
      label: t('action', { ns: 'admin/common' }),
      sortable: false,
      searchable: false,
      className: 'w-24 border-l border-white border-b-0 sticky left-12 z-10',
    },
    {
      id: 'username',
      label: t('username'),
      sortable: true,
      searchable: true,
      className: 'w-44 border-l border-[#EBF1F8] border-b-0',
    },
    {
      id: 'name',
      label: t('name'),
      sortable: true,
      searchable: true,
      className: 'w-56 border-l border-white border-b-0',
    },
    {
      id: 'roleid',
      label: t('roleid.title'),
      sortable: false,
      searchable: false,
      className: 'w-56 border-l border-white border-b-0',
    },
    {
      id: 'phone',
      label: t('phone'),
      sortable: true,
      searchable: true,
      className: 'w-36 border-l border-white border-b-0',
    },
    {
      id: 'email',
      label: t('email'),
      sortable: true,
      searchable: true,
      className: 'w-44 border-l border-white border-b-0',
    },
    {
      id: 'createuser',
      label: t('createuser'),
      sortable: true,
      searchable: true,
      className: 'w-36 border-l border-white border-b-0',
    },
    {
      id: 'createdate',
      label: t('createdate'),
      sortable: true,
      searchable: false,
      className: 'w-36 border-l border-white border-b-0',
    },
  ];

  const getRoleId = (roleId?: number, provinceName?: string, wardName?: string) => {
    switch (roleId) {
      case RoleIdUserEnum.QUAN_TRI:
        return t('roleid.' + RoleIdUserEnum.QUAN_TRI);
      case RoleIdUserEnum.TINH_THANH:
        return `${t('roleid.' + RoleIdUserEnum.TINH_THANH)} ${provinceName ? `- ${provinceName}` : ''}`;
      case RoleIdUserEnum.XA_PHUONG:
        return `${t('roleid.' + RoleIdUserEnum.XA_PHUONG)} ${wardName ? `- ${wardName}` : ''}`;
      case RoleIdUserEnum.NGUOI_DEO:
        return t('roleid.' + RoleIdUserEnum.NGUOI_DEO);
      case RoleIdUserEnum.TRUNG_UONG:
        return t('roleid.' + RoleIdUserEnum.TRUNG_UONG);
      default:
        return '';
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      userActions.setFilter({
        pageIndex: newPage,
      })
    );
  };

  const handleChangePageSize = (pageSize: number) => {
    dispatch(
      userActions.setFilter({
        pageSize: pageSize,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSort = (sortBy: string, sortOrder: SortDirection) => {
    dispatch(
      userActions.setFilter({
        sortBy: sortBy,
        sortOrder: sortOrder,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  const handleRequestSearch = (searchBy: string, value: string) => {
    dispatch(
      userActions.setFilter({
        search: value,
        searchBy: searchBy,
        pageIndex: PAGE_INDEX,
      })
    );
  };

  return (
    <Box className='w-full'>
      <BaseTable<any>
        label={t('label')}
        data={users}
        pageIndex={filter.pageIndex || PAGE_INDEX}
        pageSize={filter.pageSize || PAGE_SIZE}
        totalItems={totalItems}
        headCells={headCells}
        sortBy={filter.sortBy}
        sortOrder={filter.sortOrder}
        searchInput={filter.search}
        onChangePage={handleChangePage}
        onChangePageSize={handleChangePageSize}
        onRequestSort={handleRequestSort}
        onRequestSearch={handleRequestSearch}
        renderCell={(row: IUser, index: number) => {
          return (
            <>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                align='center'
                sx={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'white' }}
              >
                {index + PAGE_INDEX + (Number(filter.pageIndex || 0) - PAGE_INDEX) * PAGE_SIZE}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ position: 'sticky', left: 48, zIndex: 1, backgroundColor: 'white' }}
              >
                <Box className='flex justify-center'>
                  <LightTooltip title={t('tooltip.reset_password')}>
                    <IconButton onClick={() => props.onResetPassword?.(row)}>
                      <RotationLock stroke='#123B6D' />
                    </IconButton>
                  </LightTooltip>
                  {row.active === ActiveEnum.OPEN ? (
                    <LightTooltip title={t('tooltip.inactive')}>
                      <IconButton onClick={() => props.onOpenActive?.(row)}>
                        <RotationLock3 />
                      </IconButton>
                    </LightTooltip>
                  ) : (
                    <LightTooltip title={t('tooltip.active')}>
                      <IconButton onClick={() => props.onOpenActive?.(row)}>
                        <RotationLock2 />
                      </IconButton>
                    </LightTooltip>
                  )}
                  <LightTooltip title={t('tooltip.edit', { ns: 'admin/common' })}>
                    <IconButton onClick={() => props.onOpenEdit?.(row)}>
                      <EditLineIcon />
                    </IconButton>
                  </LightTooltip>
                  <LightTooltip title={t('tooltip.delete', { ns: 'admin/common' })}>
                    <IconButton onClick={() => props.onOpenDelete?.(row)}>
                      <DeleteBin6LineIcon />
                    </IconButton>
                  </LightTooltip>
                </Box>
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.username)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.name)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {getRoleId(row.roleid, row.province_name, row.ward_name)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.phone)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.email)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {cast(row.createuser)}
              </TableCell>
              <TableCell
                className='text-base font-normal text-[#161A1F]'
                sx={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                align='center'
              >
                {row.createdate ? dayjs(row.createdate).format('DD/MM/YYYY') : ''}
              </TableCell>
            </>
          );
        }}
      />
    </Box>
  );
};

export default memo(UserList);
